import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useOwnerCheck } from "@/hooks/useOwnerCheck";
import { useInstitutionAdminCheck } from "@/hooks/useInstitutionAdminCheck";
import { useSchoolAdminCheck } from "@/hooks/useSchoolAdminCheck";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Menu,
  Brain,
  Building2,
  LayoutDashboard,
  FileQuestion,
  Users,
  BarChart3,
  GraduationCap,
  Stethoscope,
  Settings,
  Trophy,
  FileText,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  School,
  ShieldAlert,
  Archive,
} from "lucide-react";
import { useState, useEffect } from "react";
import { scheduleStaleOverlayRelease } from "@/lib/overlay-guard";
import { useMenuDiagnostics } from "@/hooks/useMenuDiagnostics";
import { usePendingInstitutionRequests } from "@/hooks/useInstitutionRequests";

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  roles: ("admin" | "owner" | "institution_admin" | "school_admin")[];
  badge?: { text: string; tone: "info" | "new" };
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Admin Management",
    items: [
      { label: "Users", path: "/admin-users", icon: Users, roles: ["admin"] },
      { label: "Cohorts", path: "/admin-cohorts", icon: School, roles: ["admin", "institution_admin", "school_admin"], badge: { text: "Schools", tone: "info" } },
      { label: "Institution Requests", path: "/admin-institution-requests", icon: Building2, roles: ["admin", "owner"] },
      { label: "Engagement Insights", path: "/admin/engagement-insights", icon: BarChart3, roles: ["admin", "owner"], badge: { text: "New", tone: "new" } },
      { label: "Statistics", path: "/admin-statistics", icon: BarChart3, roles: ["admin"] },
      { label: "Top Students", path: "/admin-top-students", icon: Trophy, roles: ["admin"] },
    ],
  },
  {
    label: "Question Bank",
    items: [
      { label: "Questions", path: "/admin-questions", icon: FileQuestion, roles: ["admin"] },
      { label: "Question Editor", path: "/admin/question-editor", icon: BookOpen, roles: ["admin"] },
      { label: "Question Review", path: "/admin-question-review", icon: CheckCircle, roles: ["admin"] },
      { label: "Quality Audit", path: "/admin-quality-audit", icon: AlertTriangle, roles: ["admin"] },
      { label: "Possible Duplicate Quarantine", path: "/admin-duplicate-quarantine", icon: ShieldAlert, roles: ["admin", "owner"] },
      { label: "Rework Bank", path: "/admin-rework-bank", icon: Archive, roles: ["admin", "owner"] },
      { label: "Export Questions", path: "/admin-export-questions", icon: FileText, roles: ["admin"] },
    ],
  },
  {
    label: "Academic Intelligence",
    items: [
      { label: "Content Analysis", path: "/admin-content-analysis", icon: BarChart3, roles: ["admin"] },
      { label: "Pedagogy Audit", path: "/admin-pedagogy-audit", icon: GraduationCap, roles: ["admin"] },
      { label: "Pedagogy Review Bank", path: "/admin-pedagogy-review-bank", icon: BookOpen, roles: ["admin"] },
      { label: "Blueprint Diagnostics", path: "/admin-blueprint-diagnostics", icon: AlertTriangle, roles: ["admin"] },
      { label: "Blueprint Validation", path: "/admin-blueprint-validation", icon: CheckCircle, roles: ["admin"] },
    ],
  },
  {
    label: "Anatomy",
    items: [
      { label: "Visual Anatomy", path: "/admin-visual-anatomy", icon: Stethoscope, roles: ["admin"] },
      { label: "Anatomy Hub", path: "/anatomy", icon: GraduationCap, roles: ["admin"] },
    ],
  },
  {
    label: "Institutional / Recruitment",
    items: [
      { label: "Therapy Applicants", path: "/admin-therapy-applicants", icon: FileText, roles: ["admin"] },
      { label: "Recruitment Settings", path: "/admin-recruitment-settings", icon: Settings, roles: ["admin"] },
      { label: "Translation Coverage", path: "/admin-translation-coverage", icon: FileText, roles: ["admin"] },
      { label: "MBLEx Reformulation", path: "/admin-mblex-reformulation", icon: BookOpen, roles: ["admin"] },
    ],
  },
];

const OWNER_ONLY_ITEMS: NavItem[] = [
  { label: "Academic Intelligence", path: "/admin/academic-intelligence", icon: Brain, roles: ["owner"] },
];

const INSTITUTIONAL_ITEMS: NavItem[] = [
  { label: "Institutional Management", path: "/institution", icon: Building2, roles: ["institution_admin", "school_admin"] },
];

export const AdminNavSheet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { isOwner } = useOwnerCheck();
  const { isInstitutionAdmin } = useInstitutionAdminCheck();
  const { isSchoolAdmin } = useSchoolAdminCheck();
  const [open, setOpen] = useState(false);
  const { triggerRef, log } = useMenuDiagnostics("admin-nav-sheet", { open });
  const pendingInstitutionRequests = usePendingInstitutionRequests(isAdmin || isOwner);

  // Always close + release the scroll lock when the route changes. Without this
  // the sheet can unmount mid-close and leave <body> with pointer-events:none.
  useEffect(() => {
    setOpen(false);
    return scheduleStaleOverlayRelease(`admin-route:${location.pathname}`);
  }, [location.pathname]);



  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/anatomy");

  // Only show the menu button when on admin routes and user has some admin role
  const canAccessAnyAdmin = isAdmin || isOwner || isInstitutionAdmin || isSchoolAdmin;
  if (!isAdminRoute || !canAccessAnyAdmin) return null;

  const isActive = (path: string) => {
    if (path === "/admin-questions") {
      return location.pathname === path || location.pathname.startsWith("/admin/question-editor");
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleNavigate = (path: string) => {
    log(`select:${path}`);
    setOpen(false);
    scheduleStaleOverlayRelease(`admin-select:${path}`);
    window.setTimeout(() => navigate(path), 80);
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const showPending =
      item.path === "/admin-institution-requests" && pendingInstitutionRequests > 0;
    return (
      <SheetClose asChild key={item.path}>
        <button
          onClick={() => handleNavigate(item.path)}
          className={[
            "flex items-center gap-2.5 w-full px-3 py-1.5 rounded-md text-[13px] transition-colors text-left min-h-[40px]",
            active
              ? "bg-primary/15 text-primary font-semibold ring-1 ring-primary/20"
              : "text-foreground/80 hover:bg-accent hover:text-accent-foreground",
          ].join(" ")}
        >
          <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-primary" : ""}`} />
          <span className="flex-1 whitespace-normal break-words leading-tight">{item.label}</span>
          {showPending && (
            <span className="ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-primary/15 text-primary flex-shrink-0">
              {pendingInstitutionRequests}
            </span>
          )}
          {!showPending && item.badge && (
            <span
              className={[
                "ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide flex-shrink-0",
                item.badge.tone === "new"
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {item.badge.text}
            </span>
          )}
        </button>
      </SheetClose>
    );
  };

  const canSeeItem = (item: NavItem) => {
    if (item.roles.includes("admin") && isAdmin) return true;
    if (item.roles.includes("owner") && isOwner) return true;
    if (item.roles.includes("institution_admin") && isInstitutionAdmin) return true;
    if (item.roles.includes("school_admin") && isSchoolAdmin) return true;
    return false;
  };

  // Build visible groups
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(canSeeItem),
  })).filter((group) => group.items.length > 0);

  const visibleOwnerItems = isOwner ? OWNER_ONLY_ITEMS : [];
  const visibleInstitutionalItems = INSTITUTIONAL_ITEMS.filter(canSeeItem);

  const hasOwnerSection = visibleOwnerItems.length > 0;
  const hasInstitutionalSection = visibleInstitutionalItems.length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground px-2 min-h-[44px] min-w-fit whitespace-nowrap inline-flex items-center justify-center shrink-0 relative z-[9000] pointer-events-auto touch-manipulation [&_svg]:pointer-events-none"
          aria-label={t("admin.menu") || "Admin Menu"}
          data-menu-trigger="admin-nav-sheet"
          onPointerDown={(event) => log("pointerdown", event)}
          onTouchStart={(event) => log("touchstart", event)}
          onClick={(event) => log("click", event)}
        >
          <Menu className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm whitespace-nowrap">
            <span className="sm:hidden">Admin</span>
            <span className="hidden sm:inline">Admin Menu</span>
          </span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] sm:w-[320px] p-0 flex flex-col h-[100dvh]">
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <SheetTitle className="text-base flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            {t("admin.panel") || "Admin Panel"}
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2 pb-[max(env(safe-area-inset-bottom),16px)] space-y-2">
            {/* Owner-only section */}
            {hasOwnerSection && (
              <div className="space-y-0.5">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                  {t("admin.ownerOnly") || "Owner"}
                </h3>
                {visibleOwnerItems.map(renderNavItem)}
              </div>
            )}

            {hasOwnerSection && visibleGroups.length > 0 && <Separator />}

            {/* Nav groups */}
            {visibleGroups.map((group, index) => (
              <div key={group.label}>
                {index > 0 && <Separator className="mb-2" />}
                <div className="space-y-0.5">
                  <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                    {group.label}
                  </h3>
                  {group.items.map(renderNavItem)}
                </div>
              </div>
            ))}

            {visibleGroups.length > 0 && hasInstitutionalSection && <Separator />}

            {/* Institutional section */}
            {hasInstitutionalSection && (
              <div className="space-y-0.5">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                  {t("admin.institutional") || "Institutional"}
                </h3>
                {visibleInstitutionalItems.map(renderNavItem)}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
